import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import { useCombinationAnswers } from '@/data/hook/question/answers/useCombinationAnswers';
import { PlusIcon } from '@heroicons/react/24/outline';
import { CombinationFormFields } from './CombinationForm';
import CombinationAnswerItem from './CombinationAnswerItem';

export default function AnswerColumnForm() {
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  const { watch } = useFormContext<CombinationFormFields>();
  const liveAnswers = watch('answers');
  const questions = watch('questions');
  const totalQuestions = questions?.length ?? 0;

  const {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleUpdateValidPositions,
    handleMarkIncorrect,
    handleDelete,
    isNewItem,
  } = useCombinationAnswers();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold">{tCommon('answersTitle')}</h3>
        <ConditionalRenderer condition={!!error?.root?.message} fallback={null}>
          <Fieldset.Label className="text-error">
            {error?.root?.message}
          </Fieldset.Label>
        </ConditionalRenderer>
      </div>

      <Button
        type="button"
        variant="soft"
        color="primary"
        className="self-start"
        onClick={handleAdd}
      >
        <PlusIcon className="size-3.5" />
        {tCommon('addOption')}
      </Button>

      <div className="flex flex-wrap gap-6">
        {fields.map((field, index) => (
          <CombinationAnswerItem
            key={field.fieldId}
            item={liveAnswers?.[index] ?? field}
            totalQuestions={totalQuestions}
            initialEditing={isNewItem({ index })}
            onUpdateText={text => handleUpdateText(index, text)}
            onUpdateMedia={(file, altText) =>
              handleUpdateMedia(index, file, altText)
            }
            onUpdateValidPositions={positions =>
              handleUpdateValidPositions(index, positions)
            }
            onMarkIncorrect={() => handleMarkIncorrect(index)}
            onDelete={() => handleDelete(index)}
          />
        ))}
      </div>
    </div>
  );
}
